import React, { useMemo, useState, useCallback } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import * as Constants from "common/constants";
import { useTranslation } from "react-i18next";
import DataContainer from "../components/common/DataContainer/DataContainer";
import { useAuth } from "../hooks/useAuth";
import { useTableColumns } from "../hooks/useTableColumns";
import { useWorkflowAccess } from "../hooks/useWorkflowAccess";

import { startWorkflowProcessByKeyThunk, cloneWorkflowThunk } from "store/thunks/workflowThunks";
import { Box, Typography, Divider, Alert } from "@mui/material";
import { CalendarToday as CalendarIcon } from "@mui/icons-material";
import LoadingBackdrop from "../components/common/custom-component/LoadingBackdrop";
import Loader from "../components/common/custom-component/Loader";
import { logger } from "helpers/logger";
function WorkflowListPage() {
	// #region STATE
	const keycloakAuth = useAuth();
	const navigate = useNavigate();
	const [t] = useTranslation();
	const dispatch = useDispatch();

	const [isLoadingBackdrop, setIsLoadingBackdrop] = useState(false);

	// Rows are the workflows the user can see in the selected company (role grant + company
	// switch + active + current application), each with its usable permission codes. Cached
	// across visits and refreshed in the background - see useWorkflowAccess.
	const { workflows, canUse, isLoading, isRefreshing, error } = useWorkflowAccess();
	const userId = keycloakAuth.user.id;
	// #endregion

	// #region Start Process
	const handleStartProcess = useCallback(async (definitionKey) => {
		setIsLoadingBackdrop(true);
		const startProcessResponse = await dispatch(
			startWorkflowProcessByKeyThunk({
				definitionKey,
				assignee: userId,
			})
		);
		if (startWorkflowProcessByKeyThunk.fulfilled.match(startProcessResponse)) {
			const userTaskResponse = startProcessResponse.payload?.data?.getCurrentUserTaskResponse;
			const formByTask = userTaskResponse?.getFormByTaskResponse;
			const formId = formByTask?.key;
			const processInstanceId = formByTask?.processInstanceId || userTaskResponse?.processInstanceId;
			const taskId = userTaskResponse?.id || formByTask?.taskId;

			setIsLoadingBackdrop(false);

			navigate("/formRenderer", {
				state: {
					submissions: [
						{
							formId: formId,
							submissionId: null,
							processInstanceId: processInstanceId,
							taskId: taskId,
							data: {},
						},
					],
					taskId: taskId,
					readOnly: false,
					title: t("Start Workflow"),
				},
			});
		} else {
			logger.error("Failed to start process");
			setIsLoadingBackdrop(false);
		}
	}, [dispatch, userId, navigate, t]);
	// #endregion

	// #region CLONE
	const handleCloneLastWorkflow = useCallback(async (applicationId) => {
		try {
			const result = await dispatch(cloneWorkflowThunk({ applicationId: applicationId, assignee: userId }));
			if (cloneWorkflowThunk.fulfilled.match(result) && result.payload?.data?.httpResponse.status == 1) {
				let formData = result.payload?.data?.startProcessResponse?.formData;
				localStorage.setItem(Constants.CurrentWorkflowStep, JSON.stringify(formData));
				navigate("/formRenderer", { state: { data: formData } });
			} else {
				logger.error("Failed to clone workflow");
				setIsLoadingBackdrop(false);
			}
		} catch (error) {
			logger.error(error);
			setIsLoadingBackdrop(false);
		}
	}, [dispatch, userId, navigate]);
	// #endregion

	// #region TABLE CONFIG

	const actions = useMemo(() => ({
		onStart: (row) => handleStartProcess(row.processDefinitionKey),
		onClone: (row) => handleCloneLastWorkflow(row.processDefinitionKey),
	}), [handleStartProcess, handleCloneLastWorkflow]);

	// Button rules in tbl_table_column_configs use the "hasWorkflowPermission" operator,
	// which calls canUse(permissionCode, row.processDefinitionKey).
	const context = useMemo(() => ({ canUse }), [canUse]);

	const { columns, isLoading: columnsLoading } = useTableColumns('WorkflowList', { actions, context });

	// #endregion

	// #region RENDER

	return (
		<Box sx={{ p: 3 }}>
			<Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
				<Typography variant="h3" color="primary">
					{t("Workflow List")}
				</Typography>
				{isRefreshing && (
					<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
						<Loader variant="circular" message="Loading..." size="small" />
					</Box>
				)}
			</Box>

			<Typography variant="body1" color="text.secondary" paragraph>
				{t("View and manage available workflow processes.")}
			</Typography>

			<Divider sx={{ my: 3 }} />

			{/* Rows or columns not there yet: nothing to draw, so the table area waits. */}
			{isLoading || columnsLoading ? (
				<Box sx={{ display: "flex", justifyContent: "center", p: 5 }}>
					<Loader variant="dots" message="Loading..." />
				</Box>
			) : error && workflows.length === 0 ? (
				<Alert severity="error">{t("Failed to load workflows.")}</Alert>
			) : workflows.length === 0 ? (
				<DataContainer data={[]} emptyStateIcon={CalendarIcon} emptyStateTitle="No Workflows Available" emptyStateSubtitle="" />
			) : (
				<Box sx={{ mt: 2 }}>
					<DataContainer data={workflows} columns={columns} showRowNumbers={true} maxHeight={600} stickyHeader={true} />
				</Box>
			)}
			<LoadingBackdrop open={isLoadingBackdrop} message="Loading data, please wait..." variant="bars" color="greenGradient" />
		</Box>
	);

	// #endregion
}

export default WorkflowListPage;
